import { NodeDescriptor } from "./source";

/**
 * A data extractor is a structure which, given an item, is able to extract a value from it.
 * It is an essential component of the data view.
 *
 * @remarks
 *
 * Each extractor used in a data view should have a unique id.
 *
 * Example:
 *
 * ```typescript
 * const items = [
 *     { id: 1, name: "Jane" },
 *     { id: 2, name: "John" },
 * ];
 *
 * const nameExtractor = {
 *     id: "name-extractor",
 *     label: "Name",
 *     getValue: item => item.name,
 *     compare: (item1, item2) => item1.localeCompare(item2),
 *     asString: item => item.name,
 * };
 *
 * ```
 */
interface DataExtractor<ITEM, VAL, FV = VAL> {
    id: string;
    label: string;
    getValue: (item: ITEM) => VAL | undefined;
    compare: (item1: ITEM, item2: ITEM) => number;
    asString: (item: ITEM) => string;
    customFilterTransformer?: CustomFilterTransformer<ITEM, FV>;
}

interface CustomFilterTransformer<ITEM, FV> {
    getValue: (item: ITEM) => FV;
    asString: (filterValue: FV) => string;
}

function id(str: string): string {
    return str;
}

interface DataExtractorLike<ITEM, FV> {
    asString: (item: ITEM) => string;
    customFilterTransformer?: CustomFilterTransformer<ITEM, FV>;
}

function customOrDefaultFilterTransformer<ITEM, FV>(
    extractor: DataExtractorLike<ITEM, FV>
): CustomFilterTransformer<ITEM, FV> {
    // It's OK to only expose FV here since it's either known (and therefore defined) or unknown (and string is a good enough unknown value).
    // TODO: Check if that assessment is true...
    return extractor.customFilterTransformer
        ? extractor.customFilterTransformer
        : ({
              getValue: extractor.asString,
              asString: id,
          } as unknown as CustomFilterTransformer<ITEM, FV>);
}

/**
 * A group of extractors.
 */
interface DataExtractorGroup<ITEM> {
    id: string;
    label: string;
    extractors: Array<DataExtractor<ITEM, any>>;
}

/**
 * Given an extractor for a node, returns an extractor for a node descriptor.
 */
function toNodeExtractor<NODE, VAL>(
    dataExtractor: DataExtractor<NODE, VAL>
): DataExtractor<NodeDescriptor<NODE>, VAL> {
    return {
        id: dataExtractor.id,
        label: dataExtractor.label,
        getValue: (nodeDesc: NodeDescriptor<NODE>) => {
            return dataExtractor.getValue(nodeDesc.value);
        },
        compare: (
            nodeDesc1: NodeDescriptor<NODE>,
            nodeDesc2: NodeDescriptor<NODE>
        ) => {
            // Only nodes with the same ancestors can be compared
            if (
                nodeDesc1.ancestors.length === nodeDesc2.ancestors.length &&
                (nodeDesc1.ancestors.length === 0 ||
                    nodeDesc1.ancestors[nodeDesc1.ancestors.length - 1] ===
                        nodeDesc2.ancestors[nodeDesc2.ancestors.length - 1])
            ) {
                const result = dataExtractor.compare(
                    nodeDesc1.value,
                    nodeDesc2.value
                );
                // If the compare function returns 0, fallback on index-based
                // comparison (stable sort)
                if (result !== 0) {
                    return result;
                }
            }
            if (nodeDesc1.index === nodeDesc2.index) {
                return 0;
            } else {
                return nodeDesc1.index < nodeDesc2.index ? -1 : 1;
            }
        },
        asString: (nodeDesc: NodeDescriptor<NODE>) => {
            return dataExtractor.asString(nodeDesc.value);
        },
    };
}

/**
 * Creates node descriptor extractors from a list of node extractors.
 */
function toNodeExtractors<NODE>(
    dataExtractors: Array<DataExtractor<NODE, any>>
) {
    return dataExtractors.map(toNodeExtractor);
}

/**
 * Memoize the core function of an extractor.
 *
 * @param getValue - a typical {getValue} function of an extractor.
 * @param getItemId - a typical {getItemId} function of an data source.
 * @return the memoized version of {getValue}.
 */
function cached<ITEM, VAL>(
    getValue: (item: ITEM) => VAL,
    getItemId: (item: ITEM) => string
): (item: ITEM) => VAL {
    const itemIdToValue: { [id: string]: VAL } = {};
    return (item) => {
        const itemId = getItemId(item);
        let value = itemIdToValue[itemId];
        if (value === undefined) {
            value = getValue(item);
            itemIdToValue[itemId] = value;
        }
        return value;
    };
}

/**
 * Given two extractors and a function to combine their {getValue} functions, returns a new extractor.
 */
function combine2<ITEM, VAL1, VAL2, NewVal>(
    ext1: DataExtractor<ITEM, VAL1>,
    ext2: DataExtractor<ITEM, VAL2>,
    id: string,
    label: string,
    combine: (
        getItem1: (item: ITEM) => VAL1 | undefined,
        getItem2: (item: ITEM) => VAL2 | undefined
    ) => (item: ITEM) => NewVal
): DataExtractor<ITEM, NewVal> {
    const getValue = combine(
        (item) => ext1.getValue(item),
        (item) => ext2.getValue(item)
    );
    return {
        id,
        label,
        compare: (item1: ITEM, item2: ITEM) => {
            const ext1Result = ext1.compare(item1, item2);
            if (ext1Result !== 0) {
                return ext1Result;
            }
            return ext2.compare(item1, item2);
        },
        asString: (item: ITEM) => {
            return ext1.asString(item);
        },
        getValue,
    };
}

export {
    customOrDefaultFilterTransformer,
    type CustomFilterTransformer,
    type DataExtractorGroup,
    type DataExtractor,
    toNodeExtractor,
    toNodeExtractors,
    cached,
    combine2,
};
